import React from "react";
import { MapPin, Phone, Mail, Printer, Globe, Building2 } from "lucide-react";
import { PageHero, PageBody, STATIC_CONTENT } from "./_shared";
import InstituteMap from "@/components/InstituteMap";
import { usePoly } from "./PolyLayout";

function ContactCard({ data, accentColor }) {
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className={`bg-gradient-to-r ${accentColor} text-white p-5`}>
        <div className="w-10 h-10 rounded-md bg-white/20 backdrop-blur flex items-center justify-center mb-2">
          <Building2 size={20} strokeWidth={1.5} />
        </div>
        <h3 className="font-serif text-xl">{data.title}</h3>
      </div>
      <div className="p-6 space-y-3 text-sm">
        <div className="flex items-start gap-3">
          <MapPin size={16} strokeWidth={1.5} className="text-amber-600 mt-0.5 shrink-0" />
          <span className="text-slate-700">{data.address}</span>
        </div>
        <div className="flex items-start gap-3">
          <Phone size={16} strokeWidth={1.5} className="text-amber-600 mt-0.5" />
          <span className="text-slate-700">{(data.phones || []).join(" · ")}</span>
        </div>
        {data.fax && (
          <div className="flex items-start gap-3">
            <Printer size={16} strokeWidth={1.5} className="text-amber-600 mt-0.5" />
            <span className="text-slate-700">{data.fax}</span>
          </div>
        )}
        {data.email && (
          <div className="flex items-start gap-3">
            <Mail size={16} strokeWidth={1.5} className="text-amber-600 mt-0.5" />
            <a href={`mailto:${data.email}`} className="text-blue-700 hover:underline">
              {data.email}
            </a>
          </div>
        )}
        {data.website && (
          <div className="flex items-start gap-3">
            <Globe size={16} strokeWidth={1.5} className="text-amber-600 mt-0.5" />
            <a
              href={data.website.startsWith("http") ? data.website : `https://${data.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 hover:underline"
            >
              {data.website}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Contact() {
  const data = usePoly();
  const inst = (data && data.institute) || {};
  const address = [inst.address1, inst.address2, inst.city, inst.state, inst.country]
    .filter(Boolean)
    .join(", ");
  return (
    <div data-testid="poly-contact">
      <PageHero eyebrow="Get in Touch" title="Contact Us" accent="blue" />
      <PageBody>
        <div className="grid md:grid-cols-2 gap-6">
          <ContactCard
            data={STATIC_CONTENT.contact}
            accentColor="from-blue-700 to-indigo-700"
          />
          <ContactCard
            data={STATIC_CONTENT.contact.head_office}
            accentColor="from-amber-600 to-orange-600"
          />
        </div>
        {(inst.latitude && inst.longitude) || address ? (
          <div className="mt-8">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Find us on map</div>
            <InstituteMap latitude={inst.latitude} longitude={inst.longitude} address={address} testid="poly-contact-map" />
          </div>
        ) : null}
      </PageBody>
    </div>
  );
}
